import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import pricingContextModuleHelpers from '@vp/pricing-context-module/helpers'

const getPricingContextVatInclusivity = (): string => {
  const doc = getDocument()
  return doc?.pcxtV3?.isVatInclusive() ?? ''
}

const getPricingContext = (): any => {
  const doc = getDocument()
  return doc?.pcxtV3 ?? null
}

const isPricingContextInitialized = (developmentMode?: boolean): boolean => {
  const doc = getDocument()
  return developmentMode ? true : (doc?.pcxtV3?.isInitialized() ?? false)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDocument = (): any => {
  return typeof document !== 'undefined' ? document : undefined
}

// Types and interfaces
interface PricingContextState {
  pricingContext: any
  vatIncl: string
  pricingContextInitialized: boolean
}

interface PricingContextValue extends PricingContextState {
  setPricingContext: (context: any) => void
  setVatIncl: (vatIncl: string) => void
  refreshPricingContext: () => void
}

interface PricingContextProviderProps {
  children: ReactNode
  tenant: string
  locale: string
  developmentMode?: boolean
}

const PricingContextState = createContext<PricingContextValue | null>(null)

export const usePricingContext = (): PricingContextValue => {
  const context = useContext(PricingContextState)
  if (context === null) {
    throw new Error('usePricingContext must be used within a PricingContextProvider')
  }
  return context
}

export const PricingContextProvider = ({ children, tenant, locale, developmentMode = false }) => {
  const [pricingContextInitialized, setPricingContextInitialized] = useState(false)
  const [pricingContext, setPricingContextState] = useState<any>(null)
  const [vatIncl, setVatInclState] = useState<string>('')

  // Wrapper functions to update state
  const setPricingContext = (context: any) => {
    setPricingContextState(context)
  }

  const setVatIncl = (vat: string) => {
    setVatInclState(vat)
  }

  const refreshPricingContext = () => {
    if (isPricingContextInitialized(developmentMode)) {
      setPricingContext(getPricingContext())
      setVatIncl(getPricingContextVatInclusivity())
    }
  }

  useEffect(() => {
    const handleInitializedEvent = () => {
      const initialized = isPricingContextInitialized(developmentMode)
      setPricingContextInitialized(initialized)
      if (initialized) {
        setPricingContext(getPricingContext())
        setVatIncl(getPricingContextVatInclusivity())
      }
    }

    const updateVatInclusivity = () => {
      setVatIncl(getPricingContextVatInclusivity())
    }

    const updatePricingContext = () => {
      setPricingContext(getPricingContext())
    }

    /** init event listener to be added only if pricing context was not initialized yet */
    if (isPricingContextInitialized(developmentMode)) {
      handleInitializedEvent()
    } else {
      document.addEventListener(pricingContextModuleHelpers.EVENTS.INITIALIZED_EVENT, handleInitializedEvent)
    }

    document.addEventListener(pricingContextModuleHelpers.EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT, updateVatInclusivity)

    document.addEventListener(pricingContextModuleHelpers.EVENTS.CONTEXT_CHANGE_EVENT, updatePricingContext)

    return () => {
      if (!isPricingContextInitialized(developmentMode)) {
        document.removeEventListener(pricingContextModuleHelpers.EVENTS.INITIALIZED_EVENT, handleInitializedEvent)
      }
      document.removeEventListener(pricingContextModuleHelpers.EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT, updateVatInclusivity)
      document.removeEventListener(pricingContextModuleHelpers.EVENTS.CONTEXT_CHANGE_EVENT, updatePricingContext)
    }
  }, [tenant, locale, developmentMode])

  const contextValue: PricingContextValue = {
    pricingContext,
    vatIncl,
    pricingContextInitialized,
    setPricingContext,
    setVatIncl,
    refreshPricingContext,
  }

  return <PricingContextState.Provider value={contextValue}>{children}</PricingContextState.Provider>
}

// Export utility functions for external use if needed
export { getPricingContext, getPricingContextVatInclusivity, isPricingContextInitialized }

// Default export to match filename pattern (required by build system)
const PricingContext = {
  PricingContextProvider,
  usePricingContext,
  getPricingContext,
  getPricingContextVatInclusivity,
  isPricingContextInitialized,
}

export default PricingContext
